/* ==========================================================================
   fk-availability.js — the availability line's state machine.

   Writes ONE sentence and the store control. See the header of
   snippets/fk-availability.liquid for what this used to render and why the
   prose, the method rows and the urgency pill were cut.

   THREE states:
     A. LOCAL WIN — a store is selected AND THIS VARIANT is listed as ready at
        that store by `variant.metafields.custom.ready_stores`, the single
        field Flow writes and the shelves, the badge and this line all read.
        See the snippet header for why tags and `store_availabilities` both
        failed here.
     B. NATIONAL  — no store selected, or a local shopper looking at something
        not on their shelf while the shop is still working. The cut
        "pickup in 2-4 days" state stays cut — it read as a downgrade at the
        moment of decision.
     C. LOCAL CLOSED (operator ruling 2026-08-26) — a store IS selected, the
        item is not on today's shelf, and that shop has closed or passed its
        cutoff. The carrier sentence ("Ships tomorrow") is the wrong voice for
        a shopper in local context, so the line speaks locally instead:
        "Delivers tomorrow from Austin". Same promise shape as state A's
        closed wording — it names the store's next working day, no more.

   THE SENTENCE IS TWO HALVES, written separately:
     answer  the bold clause — "Ships today", "Ready today in Austin"
     detail  the rest, or EMPTY when there is no live deadline to quote

   Four of the seven states have no detail at all. That is the point: a
   countdown is only shown when a real cutoff is running, because inventing
   one would be inventing urgency. An em-dash joins two clauses
   ("Ships today — order in the next 2h 14m"); no separator is used where the
   phrase runs on naturally ("Ready today in Austin from 10 AM").

   All timing is America/Chicago via FK.StoreContext. Never the browser clock.
   ========================================================================== */

(function () {
  'use strict';

  function ctx() { return window.FK && window.FK.StoreContext; }
  function cfg() { return window.FKConfig || {}; }

  /* Wording comes from Theme settings -> FK — Availability line, carried in
     by fk-store-data. A blank setting falls back to the shipped default, so
     the line can never render empty. */
  function texts() {
    var t = cfg().availabilityText || {};
    return {
      readyToday: t.readyToday || 'Ready today in {city}',
      readyLater: t.readyLater || 'Ready {day} in {city}',
      shipsToday: t.shipsToday || 'Ships today',
      shipsLater: t.shipsLater || 'Ships {day}',
      localClosed: t.localClosed || 'Delivers {day}',
      detailCountdown: t.detailCountdown || '— order in the next {countdown}',
      detailFromTime: t.detailFromTime || 'from {time}',
      detailFromCity: t.detailFromCity || 'from {city}'
    };
  }

  function fill(tpl, vars) {
    return String(tpl).replace(/\{(city|day|time|countdown)\}/g, function (m, k) {
      return vars && vars[k] != null ? String(vars[k]) : '';
    }).replace(/\s+/g, ' ').trim();
  }

  /* The detail half always leads with one space so the two halves read as one
     sentence; an empty template collapses to nothing. */
  function detailFill(tpl, vars) {
    var out = fill(tpl, vars);
    return out ? ' ' + out : '';
  }

  /* Which stores can hand THIS variant over today.

     Server-rendered for the variant the page loaded with, and swapped from the
     inline map below whenever the shopper picks a different one. */
  function readyStores(root) {
    var raw = root.getAttribute('data-fk-ready-stores') || '';
    return raw.split(',').map(function (s) { return s.trim(); }).filter(Boolean);
  }

  /* Every variant's answer, rendered with the page as JSON.

     This is the whole payoff of reading a metafield instead of
     `store_availabilities`: a metafield is plain data, so Liquid can emit the
     map for ALL variants up front. There is no request, so switching colour
     re-answers the question in the same frame — no round-trip, no flash of the
     national answer on the way to a local one.

     Parsed once per block and kept on the element. A product whose variants
     Flow has not written yet yields nulls, which read as "collectable
     nowhere" — the safe direction to be wrong in. */
  function variantMap(root) {
    if (root._fkMap) return root._fkMap;
    var el = root.querySelector('[data-fk-availability-map]');
    var map = {};
    if (el) {
      try { map = JSON.parse(el.textContent) || {}; } catch (e) { map = {}; }
    }
    root._fkMap = map;
    return map;
  }

  function storesForVariant(root, variantId) {
    var v = variantMap(root)[String(variantId)];
    return Array.isArray(v) ? v.join(',') : '';
  }

  /* Next weekday the warehouse dispatches (Mon-Fri), as a label. */
  function nextShipDayLabel(now) {
    var DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    var offset = 1;
    var next = (now.dow + offset) % 7;
    while (next === 0 || next === 6) {
      offset += 1;
      next = (now.dow + offset) % 7;
    }
    return offset === 1 ? 'tomorrow' : DAY_NAMES[next];
  }

  /* Write only when the value actually changed. Two reasons: it stops the
     60-second tick from churning DOM that did not change, and it keeps any
     assistive-tech observer from seeing a mutation where there is no news. */
  function setText(el, value) {
    if (el && el.textContent !== value) el.textContent = value;
  }

  function render(root) {
    var C = ctx();
    if (!C) return;

    var answerEl = root.querySelector('[data-fk-availability-answer]');
    var detailEl = root.querySelector('[data-fk-availability-detail]');
    var changeEl = root.querySelector('.fk-availability__change');

    var key = C.get();
    var store = C.store();
    var onShelf = key !== 'none' && readyStores(root).indexOf(key) !== -1;

    var answer = '';
    var detail = '';
    var win = false;

    /* ---------------- State A — the local win ---------------- */
    var T = texts();

    if (onShelf && store) {
      var status = C.status(key);
      var city = store.city;

      if (status.pastCutoff || status.state === 'after-close' || status.state === 'closed') {
        /* Not today any more. Name the day rather than assuming tomorrow — a
           Saturday evening in a shop that opens Monday is not "tomorrow". */
        var dayWord = status.nextOpenDayName || 'tomorrow';
        answer = fill(T.readyLater, { day: dayWord, city: city });
        if (status.nextOpenHour != null) detail = detailFill(T.detailFromTime, { time: C.formatHour(status.nextOpenHour) });
        win = false;

      } else if (status.state === 'before-open') {
        answer = fill(T.readyToday, { city: city });
        if (status.openHour != null) detail = detailFill(T.detailFromTime, { time: C.formatHour(status.openHour) });
        win = true;

      } else {
        answer = fill(T.readyToday, { city: city });
        if (status.countdown) detail = detailFill(T.detailCountdown, { countdown: status.countdown });
        win = true;
      }

    /* ---------------- State B — national ---------------- */
    } else {
      var now = C.now();
      var cutoffHour = cfg().shipCutoffHour == null ? 14 : cfg().shipCutoffHour;
      var isWeekend = now.dow === 0 || now.dow === 6;
      var beforeCutoff = now.minutes < cutoffHour * 60;

      if (isWeekend) {
        answer = fill(T.shipsLater, { day: nextShipDayLabel(now) });
        win = false;
      } else if (beforeCutoff) {
        answer = fill(T.shipsToday, {});
        detail = detailFill(T.detailCountdown, { countdown: C.formatDuration(cutoffHour * 60 - now.minutes) });
        win = true;
      } else {
        answer = fill(T.shipsLater, { day: nextShipDayLabel(now) });
        win = false;
      }

      /* ---------------- State C — local, shop closed ----------------
         Only rewrites a NOT-today answer (win is false), only when a store is
         actually selected, and only once that shop is closed or past its own
         cutoff — the exact case the operator flagged: a local shopper reading
         "Ships tomorrow" after hours. While the shop is open, the national
         sentence stands for off-shelf items, which keeps the deliberate
         two-state behaviour for the working day. */
      if (!win && key !== 'none' && store) {
        var localStatus = C.status(key);
        if (localStatus.pastCutoff || localStatus.state === 'after-close' || localStatus.state === 'closed') {
          var localDay = localStatus.nextOpenDayName || 'tomorrow';
          answer = fill(T.localClosed, { day: localDay, city: store.city });
          detail = detailFill(T.detailFromCity, { city: store.city, day: localDay });
        }
      }
    }

    setText(answerEl, answer);
    setText(detailEl, detail);
    root.classList.toggle('fk-availability--win', win);

    /* The store control is the ONLY affordance left in the block. It says
       "Choose your store" to someone who has not picked one and "Change store"
       to someone who has — including a local shopper seeing the national
       answer because this product is not on their shelf, who most needs a way
       back. */
    if (changeEl) {
      setText(changeEl, key !== 'none' && store ? 'Change store' : 'Choose your store');
    }
  }

  function renderAll(announce) {
    document.querySelectorAll('[data-fk-availability]').forEach(function (root) {
      render(root);
      if (announce === true) announceState(root);
    });
  }

  /* Choosing a store changes the answer to "when can I have this?", which is
     worth hearing. The minute tick moves a countdown by one minute, which is
     not — so only the former writes to the live region. */
  function announceState(root) {
    var live = root.querySelector('[data-fk-availability-announce]');
    if (!live) return;
    var answer = root.querySelector('[data-fk-availability-answer]');
    var detail = root.querySelector('[data-fk-availability-detail]');
    var msg = ((answer && answer.textContent) || '') + ((detail && detail.textContent) || '');
    if (live.textContent !== msg) live.textContent = msg;
  }

  /* A different colour or size is a different shelf. Shopify stocks each
     variant separately per location, so this has to re-answer the question —
     and it is a user-initiated change, so it is worth announcing.

     Resolved straight from the inline map, so the new answer is correct on the
     first paint. The old code blanked the line to national and waited for a
     fetch; there is nothing to wait for now. */
  document.addEventListener('fk:variant-changed', function (e) {
    var id = e && e.detail && e.detail.variantId;
    if (!id) return;
    document.querySelectorAll('[data-fk-availability]').forEach(function (root) {
      if (root.getAttribute('data-fk-variant-id') === String(id)) return;
      root.setAttribute('data-fk-variant-id', String(id));
      root.setAttribute('data-fk-ready-stores', storesForVariant(root, id));
      render(root);
      announceState(root);
    });
  });

  document.addEventListener('fk:store-context-changed', function () { renderAll(true); });
  document.addEventListener('fk:tick', function () { renderAll(false); });
  document.addEventListener('shopify:section:load', function () { renderAll(false); });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', renderAll);
  } else {
    renderAll();
  }
})();
